'use client';

import { c as _c } from "react/compiler-runtime";
import { Order, SortedSet } from 'effect';
import { parseAsString, useQueryState } from 'nuqs';
import { Button } from '~/components/primitives';
import { ActivityCard } from '~/features/activities/ActivityCard';
import { useI18n } from '~/features/i18n';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { canonicalGroupIDs } from '../_lib/rules';
import { useCatalog_EditMode_Session, VIEW_TAB_QP } from './session';
type ActivityItem = TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>['activities'][number];

/** Visual state per the design handoff. Drives both the badge chip and the
 * footer button. Computed from `(catalogMode, isInIDs, passesRules)`. */
type CardState = {
  kind: 'visible';
} | {
  kind: 'excluded';
} | {
  kind: 'selected';
} | {
  kind: 'not-selected';
} | {
  kind: 'blocked';
};

/** Edit-mode card wrapper: takes an activity and renders `<ActivityCard>`
 * with a status-badge overlay, an add/remove footer menu, and a preview-
 * modal click. Self-contained — reads `session` and `?view` internally to
 * compute its own visual state per (mode, isInIDs, passesRules). The booking
 * page renders one of these per activity while editing; raw `<ActivityCard>`
 * otherwise. */
export const Catalog_EditMode_Card: React.FC<{
  activity: ActivityItem;
  /** Forwarded to `<ActivityCard>` — the user's "min participants" filter
   * (already resolved from `ctx.useFilters()` at the page level). */
  selectedParticipants: number | null;
}> = props => {
  const $ = _c(24);
  const session = useCatalog_EditMode_Session();
  const [view] = useQueryState("view", VIEW_TAB_QP);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsString.withOptions({
      shallow: false
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [, setPreview] = useQueryState("preview", t0);
  let t1;
  if ($[1] !== props.activity.groupId || $[2] !== props.activity.price || $[3] !== session.rules.activityFilters.budget || $[4] !== session.rules.activityFilters.groupIDs.IDs || $[5] !== session.rules.activityFilters.groupIDs.mode || $[6] !== view) {
    const isInIDs = (session.rules.activityFilters.groupIDs.IDs ?? []).includes(props.activity.groupId);
    const applyRulesForBlocked = view === "all";
    t1 = computeCardState({
      catalogMode: session.rules.activityFilters.groupIDs.mode,
      isInIDs,
      passesRules: applyRulesForBlocked ? activityPassesRules({
        price: props.activity.price,
        budget: session.rules.activityFilters.budget
      }) : true
    });
    $[1] = props.activity.groupId;
    $[2] = props.activity.price;
    $[3] = session.rules.activityFilters.budget;
    $[4] = session.rules.activityFilters.groupIDs.IDs;
    $[5] = session.rules.activityFilters.groupIDs.mode;
    $[6] = view;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const state = t1;
  let t2;
  if ($[8] !== props.activity.groupId || $[9] !== session) {
    t2 = () => {
      session.setRules(prev => {
        const toggled = Array.from(SortedSet.toggle(SortedSet.fromIterable(prev.activityFilters.groupIDs.IDs ?? [], Order.number), props.activity.groupId));
        return {
          ...prev,
          activityFilters: {
            ...prev.activityFilters,
            groupIDs: {
              mode: prev.activityFilters.groupIDs.mode,
              IDs: canonicalGroupIDs(prev.activityFilters.groupIDs.mode, toggled)
            }
          }
        };
      });
    };
    $[8] = props.activity.groupId;
    $[9] = session;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const toggle = t2;
  const t3 = state.kind !== "visible" && state.kind !== "selected";
  let t4;
  if ($[11] !== props.activity.slug || $[12] !== setPreview) {
    t4 = () => setPreview(props.activity.slug);
    $[11] = props.activity.slug;
    $[12] = setPreview;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== session.step || $[15] !== state || $[16] !== toggle) {
    t5 = session.step === "selection" ? <CardFooterButton state={state} onToggle={toggle} /> : null;
    $[14] = session.step;
    $[15] = state;
    $[16] = toggle;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== props.activity || $[19] !== props.selectedParticipants || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <ActivityCard {...props.activity} disableHref={true} hideWishlist={true} inactive={t3} onClick={t4} selectedParticipants={props.selectedParticipants}>{t5}</ActivityCard>;
    $[18] = props.activity;
    $[19] = props.selectedParticipants;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
};
const CardFooterButton: React.FC<{
  state: CardState;
  onToggle: () => void;
}> = props => {
  const $ = _c(8);
  const i18n = useI18n();
  const isAdding = props.state.kind !== "visible" && props.state.kind !== "selected";
  const t0 = isAdding ? "secondary" : "outline";
  const t1 = isAdding ? "plus" : "trash";
  let t2;
  if ($[0] !== i18n || $[1] !== isAdding) {
    t2 = isAdding ? i18n._("bizz.booking.edit.card.add", undefined, {
      message: "Add to catalog"
    }) : i18n._("bizz.booking.edit.card.remove", undefined, {
      message: "Remove from catalog"
    });
    $[0] = i18n;
    $[1] = isAdding;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== props.onToggle || $[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = <menu className="z-1 flex px-3 pb-3 pt-3" onClick={_temp}><Button onClick={props.onToggle} variant={t0} size="sm" iconBefore={t1} className="w-full justify-center" data-testid="bizz-booking-card-toggle">{t2}</Button></menu>;
    $[3] = props.onToggle;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};

/** Decide which visual state a card lands in given `(mode, isInIDs, passesRules)`.
 * Pure for unit-testability. */
function computeCardState(input: {
  catalogMode: 'EXCLUSIVE' | 'INCLUSIVE';
  isInIDs: boolean;
  passesRules: boolean;
}): CardState {
  if (input.catalogMode === 'EXCLUSIVE') {
    if (input.isInIDs) return {
      kind: 'excluded'
    };
    if (!input.passesRules) return {
      kind: 'blocked'
    };
    return {
      kind: 'visible'
    };
  }
  // INCLUSIVE
  if (input.isInIDs) return {
    kind: 'selected'
  };
  if (!input.passesRules) return {
    kind: 'blocked'
  };
  return {
    kind: 'not-selected'
  };
}

/** Client-side rule evaluation. Only used for the "All" tab so the `Blocked`
 * chip appears on cards that the server-side `Visible` query would have
 * filtered out. We can only evaluate the rules slices that are present in
 * the search payload — region IDs and category IDs are NOT included (the
 * server destructures them out before sending), so only the budget cap is
 * evaluable here. False negatives (a card the server would block but we
 * don't chip) are preferred over false positives per the plan. */
function activityPassesRules(input: {
  price: number;
  budget: {
    min: number | null;
    max: number | null;
  };
}): boolean {
  const price = input.price;
  if (input.budget.min !== null && price < input.budget.min) return false;
  if (input.budget.max !== null && price > input.budget.max) return false;
  return true;
}
function _temp(event) {
  return event.stopPropagation();
}